import { memo, useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CollapsibleSection } from '@/components/atoms/CollapsibleSection/CollapsibleSection';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { MenuItemsType } from '@/hooks/useViews';
import { cn } from '@/lib/utils';
import { Item, PinMenuContent } from '@/modules/humanChat/components/LeftSidebar/PinMenuContent';

import { NavItem } from './NavItem';
import { MenuItem } from '../../models';

const PLUS_BUTTON_BASE =
  'h-full w-full rounded opacity-0 transition-opacity group-hover:opacity-100 hover:bg-stroke hover:opacity-100 focus:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 [&_svg]:text-accent';

interface OptionProps {
  hasPermission?: boolean;
  showListMenu: boolean;
  items?: Item[];
  onMoreClick?: () => void;
  plusTooltip?: string;
}

const Option = memo(
  ({ hasPermission, showListMenu, items, onMoreClick, plusTooltip }: OptionProps) => {
    const [open, setOpen] = useState(false);

    const { t } = useTranslation();

    if (!hasPermission) {
      return null;
    }
    if (showListMenu && items) {
      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setOpen((prev) => !prev);
      };

      return (
        <Popover open={open} onOpenChange={setOpen}>
          <InfoTooltip content={plusTooltip} side="right">
            <PopoverTrigger asChild>
              <Button
                variant="link"
                size="icon-xss"
                className={cn(PLUS_BUTTON_BASE, { 'bg-stroke opacity-100': open })}
                onClick={handleClick}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t('common.add_menu')}
              >
                <PlusIcon size={16} />
              </Button>
            </PopoverTrigger>
          </InfoTooltip>
          <PopoverContent side="right" align="start" className="w-64 p-0">
            <PinMenuContent items={items} />
          </PopoverContent>
        </Popover>
      );
    } else if (onMoreClick) {
      return (
        <InfoTooltip content={plusTooltip} side="right">
          <Button
            variant="link"
            size="icon-xss"
            className={cn(
              PLUS_BUTTON_BASE,
              'flex justify-center [&_svg]:h-full [&_svg]:align-middle'
            )}
            onClick={onMoreClick}
          >
            <PlusIcon size={16} />
          </Button>
        </InfoTooltip>
      );
    } else {
      return null;
    }
  }
);

Option.displayName = 'Option';

export type NavListProps = {
  title: string;
  onMoreClick?: () => void;
  pinnedItems?: MenuItem[] | MenuItemsType;
  items?: Item[];
  hasPermission?: boolean;
  keepOpen?: boolean;
  open?: boolean;
  isLoading?: boolean;
  selectedItemId?: string;
  plusTooltip?: string;
};

export const NavList = memo(
  ({
    title,
    pinnedItems,
    items,
    hasPermission,
    onMoreClick,
    keepOpen = false,
    open = false,
    isLoading = false,
    selectedItemId,
    plusTooltip,
  }: NavListProps) => {
    const showListMenu = Boolean(items?.length);

    const { t } = useTranslation();

    if (!items?.length && !pinnedItems?.length && !onMoreClick) {
      return null;
    }

    return (
      <CollapsibleSection
        keepOpen={keepOpen}
        open={isLoading || open}
        title={isLoading ? <Skeleton className="h-4 w-24" /> : <>{title}</>}
        rightIcon={
          <Option
            hasPermission={hasPermission}
            items={items}
            onMoreClick={onMoreClick}
            showListMenu={showListMenu}
            plusTooltip={plusTooltip}
          />
        }
        showBorder={false}
      >
        {isLoading &&
          new Array(4).fill(null).map((_, i) => {
            // eslint-disable-next-line react/no-array-index-key
            return <Skeleton key={i} className="mb-2 h-4 w-full" />;
          })}
        {!isLoading && (
          <ul className="flex list-none flex-col gap-px p-0">
            {pinnedItems?.map((item) => {
              return (
                <NavItem
                  notificationKey={item?.notificationKey}
                  text={item?.text}
                  icon={item?.icon}
                  onClick={item?.onClick}
                  sum={item?.sum}
                  exactSum={item?.exactSum}
                  key={item?.id}
                  selected={selectedItemId === item?.id}
                  options={item?.options}
                  disabled={item?.disabled}
                  type={item?.type}
                />
              );
            })}
            {pinnedItems?.length === 0 && (
              <li className="p-1.5">
                <span className="text-sm leading-5 font-normal text-foreground-muted select-none">
                  {t(`environments.human_chat_empty_dropdown`, { 0: title })}
                </span>
              </li>
            )}
          </ul>
        )}
      </CollapsibleSection>
    );
  }
);

NavList.displayName = 'NavList';
